import type { ChipPickerChip, PickerOption } from "@cntrl/ui";
import { PersonAvatar } from "./PersonAvatar.js";
import { AvMark } from "./AvMark.js";
import { orgInitials } from "../../app/hooks/useOrg.js";
import styles from "./PersonPickerOption.module.scss";

/**
 * One PERSON row inside a picker's listbox — avatar · name · job, the job hard right.
 *
 * Rendered through `option.render`, which both `Picker` and `ChipPicker` take, so the same row serves the
 * task create modal's chip picker and the drawer's "+ Add" select. It was local to `CrewInviteePicker` until
 * the drawer needed the same list (ART-121); a second copy is how one person comes to look like two.
 *
 * The option's plain-text `label` still carries the NAME — that is what the type-ahead filters on and what
 * AT announces — so nothing here may be the only place a candidate is identified.
 */
export function PersonPickerOption({
  personId,
  displayName,
  avatarMediaId,
  jobLabel,
  roleKey,
}: {
  personId: string;
  displayName: string;
  avatarMediaId: string | null;
  jobLabel?: string;
  /** The candidate's granted role key, so a full-access band member gets the crimson AV mark beside the job
   *  (ART-171). The job text is a plain string resolved upstream (band OR org), so the mark rides alongside
   *  it rather than through RoleLabel; `AvMark` renders nothing for any non-AV role. */
  roleKey?: string | null;
}) {
  return (
    <span className={styles.option}>
      <PersonAvatar
        personId={personId}
        avatarMediaId={avatarMediaId}
        initials={orgInitials(displayName)}
        size="sm"
        shape="circle"
      />
      <span className={styles.optionName}>{displayName}</span>
      {jobLabel && (
        <span className={styles.optionRole}>
          {jobLabel}
          <AvMark roleKey={roleKey} />
        </span>
      )}
    </span>
  );
}

/** The fields a person picker row needs — the shape `useAssignableRows` / `useInvitableRows` already produce. */
export interface PersonPickerRow {
  personId: string;
  displayName: string;
  avatarMediaId: string | null;
  /** The job, resolved upstream from the TITLE (never the role). */
  jobLabel?: string;
  /** The granted role key, for the AV mark (ART-171). */
  roleKey?: string | null;
  group?: string;
}

/**
 * One person → one `Picker`/`ChipPicker` option. Every person-assignee dropdown (the task drawer, the
 * task/opportunity crew pickers, the release template's "Assigned to") maps its rows through this, so a
 * person reads the same — face, name, job, AV mark — in each of them (ART-289). The plain `label` stays the
 * NAME: it is the type-ahead key and the accessible name.
 */
export function toPersonPickerOption(row: PersonPickerRow): PickerOption {
  return {
    value: row.personId,
    label: row.displayName,
    group: row.group,
    render: (
      <PersonPickerOption
        personId={row.personId}
        displayName={row.displayName}
        avatarMediaId={row.avatarMediaId}
        jobLabel={row.jobLabel}
        roleKey={row.roleKey}
      />
    ),
  };
}

/** First token of a display name — "Ryan Ward" → "Ryan". Falls back to the whole string. */
const firstNameOf = (name: string) => name.trim().split(/\s+/)[0] || name;

/**
 * One chosen person → one `ChipPicker` chip: a small avatar + FIRST name (ART-176). `label` keeps the full
 * name, so the remove button and AT still announce who is being dropped.
 */
export function toPersonPickerChip(row: Pick<PersonPickerRow, "personId" | "displayName" | "avatarMediaId">): ChipPickerChip {
  return {
    value: row.personId,
    label: row.displayName,
    render: (
      <span className={styles.chip}>
        <PersonAvatar
          personId={row.personId}
          avatarMediaId={row.avatarMediaId}
          initials={orgInitials(row.displayName)}
          size="xs"
          shape="circle"
        />
        <span className={styles.chipName}>{firstNameOf(row.displayName)}</span>
      </span>
    ),
  };
}
